/* eslint-disable zeffy-linter-frontend/no-classname-prop -- legacy baseline (pre-rule); remove when these are migrated */
import { RefAttributes, forwardRef, Ref, type JSX, useCallback, FocusEvent, ReactElement } from "react";

import { FieldValues, useController } from "react-hook-form";

import { getLabel, isInputRequired } from "../../../common/helpers/validators";
import { useCurrentInputVibe } from "../../../common/hooks/useCurrentInputVibe";
import { useCustomError } from "../../../common/hooks/useCustomError";
import { DashboardInputLabel } from "../InputLabel/DashboardInputLabel";
import { DashboardRichTextInput } from "../RichTextInput";

import { RichTextFieldProps, FormRichTextFieldProps, BaseRichTextFieldProps } from "./types";

import { mergeRefs } from "@/common/helpers/refs";
import { useValidators } from "@/common/hooks/useValidators";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { useDebounce } from "@/components/TextField/useDebounce";

function RichTextFieldInner<T extends FieldValues>(
  { control, rules, name, shouldUnregister, ...rest }: RichTextFieldProps<T>,
  ref: Ref<HTMLDivElement>
) {
  return control && name ? (
    <FormRichTextField
      {...rest}
      ref={ref}
      control={control}
      rules={rules}
      name={name}
      shouldUnregister={shouldUnregister}
    />
  ) : (
    <BaseRichTextField {...rest} name={name} ref={ref} />
  );
}

RichTextFieldInner.displayName = "RichTextField";

export const DashboardRichTextField = forwardRef(RichTextFieldInner) as <T extends FieldValues>(
  props: RichTextFieldProps<T> & RefAttributes<HTMLInputElement>
) => JSX.Element;

function FormRichTextFieldInner<T extends FieldValues>(
  {
    control,
    debounced,
    defaultValue,
    name,
    onBlur,
    onChange,
    rules,
    shouldUnregister,
    inputRef,
    noAsterisk,
    ...rest
  }: FormRichTextFieldProps<T>,
  ref: Ref<HTMLDivElement>
) {
  const validators = useValidators(rules);
  const isRequired = isInputRequired(validators);

  const { field } = useController<T>({
    name,
    control,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    rules: validators as any,
    shouldUnregister,
  });

  const refCallBack = mergeRefs(field.ref, inputRef);

  const handleChange = useCallback(
    (value: string) => {
      onChange?.(value);
      field.onChange(value);
    },
    [field, onChange]
  );

  const handleBlur = useCallback(
    (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onBlur?.(event);
      field.onBlur();
    },
    [field, onBlur]
  );

  return (
    <BaseRichTextField
      {...rest}
      // Cannot have a controlled debounced TextField for now
      debounced={debounced}
      // Unfortunately, useController does not allow us to defined the type of field.value
      // type-coverage:ignore-next-line
      inputRef={refCallBack}
      ref={ref}
      defaultValue={defaultValue}
      onBlur={handleBlur}
      onChange={handleChange}
      data-test={rest?.["data-test"] ?? "rich-text-field"}
      label={getLabel(isRequired, rest.label, noAsterisk)}
      name={name}
    />
  );
}

FormRichTextFieldInner.displayName = "FormRichTextField";

export const FormRichTextField = forwardRef(FormRichTextFieldInner) as unknown as <T extends FieldValues>(
  props: FormRichTextFieldProps<T> & RefAttributes<HTMLDivElement>
) => JSX.Element;

export const BaseRichTextField = forwardRef<HTMLDivElement, BaseRichTextFieldProps>(
  (
    {
      className,
      vibe,
      debounced,
      defaultValue,
      error,
      errorMessage,
      helperText,
      headerRight,
      label,
      onChange,
      onChangeNotDebounced,
      tooltipText,
      "data-test": dataTest,
      ...rest
    },
    ref
  ): ReactElement<BaseRichTextFieldProps> => {
    const currentInputVibe = useCurrentInputVibe(vibe);

    const currentError = useCustomError(error, errorMessage);

    const { setDebouncedValue } = useDebounce({
      debounced,
      onChange,
    });

    const handleChangeDebounced = useCallback(
      (value: string) => {
        onChangeNotDebounced?.(value);
        setDebouncedValue(value);
      },
      [onChangeNotDebounced, setDebouncedValue]
    );

    return (
      <DashboardInputLabel
        className={className}
        vibe={currentInputVibe}
        data-test={dataTest}
        errorMessage={errorMessage}
        helperText={helperText}
        headerRight={headerRight}
        label={label}
        tooltipText={tooltipText}
      >
        <DashboardRichTextInput
          {...rest}
          ref={ref}
          error={currentError}
          defaultValue={defaultValue}
          onChange={debounced ? handleChangeDebounced : onChange}
        />
      </DashboardInputLabel>
    );
  }
);

BaseRichTextField.displayName = "BaseRichTextField";
